import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, LinearProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { getFileCategory, getFileIconConfig, formatFileSize } from './chatUtils';

const PendingFilePreview = ({ file, uploadProgress, onRemove }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const category = getFileCategory(file?.type);
  const { Icon: FileIcon, color: iconBg } = getFileIconConfig(category);
  const isUploading = uploadProgress !== null;
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (category === 'image' && file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [file, category]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.25,
        borderRadius: 2,
        mb: 1,
        bgcolor: isDark
          ? alpha(theme.palette.primary.main, 0.12)
          : alpha(theme.palette.primary.main, 0.07),
        border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
        position: 'relative',
      }}
    >
      {category === 'image' && previewUrl ? (
        <Box
          component="img"
          src={previewUrl}
          alt="preview"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FileIcon sx={{ fontSize: 22, color: 'white' }} />
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 600,
            fontSize: '0.78rem',
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.name}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
          {formatFileSize(file.size)}
        </Typography>
        {isUploading && (
          <Box sx={{ mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                borderRadius: 4,
                height: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: 'primary.main',
                fontWeight: 600,
              }}
            >
              Uploading {uploadProgress}%
            </Typography>
          </Box>
        )}
        {!isUploading && (
          <Typography
            variant="caption"
            sx={{ fontSize: '0.65rem', color: 'primary.main', px: 0.5 }}
          >
            Ready to send
          </Typography>
        )}
      </Box>

      {!isUploading && (
        <Tooltip title="Remove attachment">
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default PendingFilePreview;
